// src/components/AdvancedTools.tsx
import React, { useState, useEffect } from 'react';
import {
  Zap, AlertCircle, CheckCircle, Loader2, TrendingUp,
  Wallet, Target, Lock, Unlock, Download, Upload, Key,
  Shield, Settings, ArrowRight, Plus, X, Copy, ExternalLink,
  DollarSign, RefreshCw, Eye, EyeOff, Info, ChevronDown,
  ChevronUp, Activity, PieChart, Send, ArrowUpRight, ArrowDownRight,
  Rocket, Package, BarChart3, Trash, Trash2
} from 'lucide-react';
import { useWallet } from '../contexts/WalletContext';
import { bundlerService } from '../services/frontend-bundler/bundler.service';
import { PublicKey, Transaction } from '@solana/web3.js';
import solanaService from '../services/solana.service';
import ProductionTokenSniper from './ProductionTokenSniper';

interface BundlerConfig {
  walletCount: number | string;
  amount: number | string;
  delay: number;
  stealthMode: boolean;
}

interface TokenInfo {
  symbol: string;
  name: string;
  mint: string;
  decimals: number;
  price?: number;
  logoURI?: string;
}

const MIN_PASSWORD_LENGTH = 4;

// --- Helpers for robust PublicKey handling ---
const getAddressString = (key: unknown): string => {
  if (typeof key === 'string') return key;
  if (key && typeof (key as PublicKey).toBase58 === 'function') {
    return (key as PublicKey).toBase58();
  }
  throw new Error('Invalid publicKey type');
};

const getPublicKeyObj = (key: unknown): PublicKey | null => {
  if (!key) return null;
  if (key instanceof PublicKey) return key;
  if (typeof key === 'string') {
    try {
      return new PublicKey(key);
    } catch {
      return null;
    }
  }
  if (typeof (key as any)?.toBase58 === 'function') return key as PublicKey;
  return null;
};

const formatAddress = (address: string) => {
  return `${address.slice(0, 4)}...${address.slice(-4)}`;
};

const AdvancedTools: React.FC = () => {
  const { isConnected, publicKey } = useWallet();

  // Bundle State
  const [currentBundle, setCurrentBundle] = useState<any>(null);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [bundles, setBundles] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'overview' | 'create' | 'manage' | 'trade'>('overview');
  
  // UI State
  const [bundlerConfig, setBundlerConfig] = useState<BundlerConfig>({
    walletCount: 10,
    amount: 0.1,
    delay: 2000,
    stealthMode: false
  });
  const [bundlerStatus, setBundlerStatus] = useState<
    'idle' | 'creating' | 'funding' | 'ready' | 'purchasing' | 'withdrawing'
  >('idle');
  const [progress, setProgress] = useState(0);
  const [showDetails, setShowDetails] = useState<string | null>(null);
  
  // Password Management
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordAction, setPasswordAction] = useState<'create' | 'unlock' | 'export'>('create');
  const [bundleName, setBundleName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // Trading State
  const [tokenAddress, setTokenAddress] = useState('');
  const [tradeAmount, setTradeAmount] = useState('0.1');
  const [slippage, setSlippage] = useState('5');
  const [selectedToken, setSelectedToken] = useState<TokenInfo | null>(null);
  const [isLoadingToken, setIsLoadingToken] = useState(false);
  
  // Stats
  const [bundlerStats, setBundlerStats] = useState({
    totalBundles: 0,
    totalWallets: 0,
    totalBalance: 0,
    activeWallets: 0
  });

 
  useEffect(() => {
    if (isConnected && publicKey) {
      loadUserBundles();
    }
  }, [isConnected, publicKey]);

  // In AdvancedTools component
useEffect(() => {
  const checkSession = async () => {
    if (isConnected && publicKey) {
      // Load user bundles
      await loadUserBundles();
      
      // Check if there's a restored session
      const currentBundle = bundlerService.getCurrentBundle();
      if (currentBundle && !bundlerService.getIsLocked()) {
        setCurrentBundle(currentBundle);
        setIsUnlocked(true);
        setBundlerStatus('ready');
        console.log('✅ Restored active bundle from session');
      }
    }
  };
  
  checkSession();
}, [isConnected, publicKey]);

  const loadUserBundles = async () => {
  if (!publicKey) return;

  try {
    const walletAddress = getAddressString(publicKey);
    const userBundles = await bundlerService.getUserBundles(walletAddress);
    
    // Force state update
    setBundles(userBundles);

    const stats = {
      totalBundles: userBundles.length,
      totalWallets: userBundles.reduce((sum, b) => sum + b.wallets.length, 0),
      totalBalance: userBundles.reduce((sum, b) => sum + b.totalBalance, 0),
      activeWallets: userBundles.reduce((sum, b) =>
        sum + b.wallets.filter((w: any) => w.funded).length, 0
      )
    };
    setBundlerStats(stats);
    
    console.log('Loaded bundles:', userBundles.length);
  } catch (error) {
    console.error('Failed to load bundles:', error);
    // Set empty state on error
    setBundles([]);
    setBundlerStats({
      totalBundles: 0,
      totalWallets: 0,
      totalBalance: 0,
      activeWallets: 0
    });
  }
};
  // Add a method to verify bundle state before operations
  const verifyBundleState = async (): Promise<boolean> => {
    if (!currentBundle || !isUnlocked) {
      console.log('No bundle or not unlocked');
      return false;
    }

    // Check if the bundler service still has the bundle in memory
    try {
      // Try to refresh balances as a way to verify the bundle is still active
      await bundlerService.refreshBalances();
      return true;
    } catch (error) {
      console.error('Bundle state lost in service:', error);
      // Try to re-unlock the bundle
      setIsUnlocked(false);
      alert('Bundle session expired. Please unlock again.');
      return false;
    }
  };

  const handleCreateBundle = () => {
    setPasswordAction('create');
    setShowPasswordModal(true);
  };

  const createBundle = async () => {
  if (!publicKey || !password || !bundleName) return;

  if (password.length < MIN_PASSWORD_LENGTH) {
    alert(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
    return;
  }

  if (passwordAction === 'create' && password !== confirmPassword) {
    alert('Passwords do not match');
    return;
  }

  // Convert string inputs to numbers and validate
  const walletCount = Number(bundlerConfig.walletCount) || 1;
  const amount = Number(bundlerConfig.amount) || 0;
  
  if (walletCount < 1 || walletCount > 50) {
    alert('Number of wallets must be between 1 and 50');
    return;
  }

  if (amount < 0) {
    alert('Amount cannot be negative');
    return;
  }

  setBundlerStatus('creating');
  setProgress(0);

  try {
    const walletAddress = getAddressString(publicKey);

    const bundle = await bundlerService.createBundle(
      bundleName,
      walletAddress,
      walletCount, // Use the converted number
      password,
      {
        stealthMode: bundlerConfig.stealthMode,
        maxSlippage: 5,
        priorityFee: 0.0000001,
        distributeEvenly: true
      }
    );

    setCurrentBundle(bundle);
    setIsUnlocked(true);
    setProgress(50);

    // Close modal and reset fields
    setShowPasswordModal(false);
    resetPasswordFields();

    // Fund wallets if amount > 0
    if (amount > 0) { // Use the converted number
      setBundlerStatus('funding');

      try {
        // Pass the bundle directly instead of relying on state
        const result = await fundBundleWalletsWithBundle(
          bundle,  // Pass the bundle here
          amount, // Use the converted number
          (prog: number) => setProgress(50 + prog * 0.5)
        );

        if (result?.success) {
          setBundlerStatus('ready');
          const fundedCount = result.funded;
          setTimeout(() => {
            alert(`✅ Bundle created successfully!\n${fundedCount} wallets funded.`);
          }, 100);
        } else {
          const errorMsg = result?.error ?? 'Unknown error';
          setTimeout(() => {
            alert(`⚠️ Bundle created but funding failed: ${errorMsg}`);
          }, 100);
        }
      } catch (error: any) {
        console.error('Funding error:', error);
        setTimeout(() => {
          alert(`⚠️ Bundle created but funding failed: ${error.message}`);
        }, 100);
      }
    } else {
      setBundlerStatus('ready');
      setProgress(100);
      const walletCount = bundle.wallets.length;
      setTimeout(() => {
        alert(`✅ Bundle created successfully!\n${walletCount} wallets generated.`);
      }, 100);
    }

    await loadUserBundles();
    setActiveTab('manage');
    
  } catch (error: any) {
    console.error('Bundle creation error:', error);
    setShowPasswordModal(false);
    resetPasswordFields();
    setBundlerStatus('idle');
    setTimeout(() => {
      alert(`Failed to create bundle: ${error.message}`);
    }, 100);
  }
};

// Final version of fundBundleWalletsWithBundle that properly handles mobile:

const fundBundleWalletsWithBundle = async (
  bundle: any,
  amountPerWallet: number,
  onProgress?: (progress: number) => void
): Promise<{ success: boolean; funded: number; failed: number; error?: string }> => {
  console.log('Starting fundBundleWalletsWithBundle...', bundle);
  
  if (!bundle) {
    return { success: false, funded: 0, failed: 0, error: 'No bundle provided' };
  }
  
  if (!publicKey) {
    return { success: false, funded: 0, failed: 0, error: 'No wallet connected' };
  }

  try {
    const walletAddress = getAddressString(publicKey);
    const walletPubKey = new PublicKey(walletAddress);
    
    console.log('Wallet public key:', walletPubKey.toBase58());
    console.log('Number of wallets to fund:', bundle.wallets.length);

    // Check if we're on mobile
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const provider = (window as any).solana;
    const isInPhantomBrowser = !!(provider?.isPhantom && isMobile);

    if (isMobile && !isInPhantomBrowser) {
      // 📱 MOBILE: Use single transaction method
      console.log('📱 Mobile detected: Using single transaction for all wallets');
      
      // Check wallet count limit for mobile
      if (bundle.wallets.length > 20) {
        const proceed = window.confirm(
          `You have ${bundle.wallets.length} wallets, but mobile can only handle up to 20 wallets in a single transaction.\n\n` +
          `Would you like to fund only the first 20 wallets?`
        );
        
        if (!proceed) {
          return {
            success: false,
            funded: 0,
            failed: bundle.wallets.length,
            error: 'Cancelled by user'
          };
        }
      }
      
      // For mobile, we need to handle the redirect flow differently
      try {
        // Store the funding parameters for after redirect
        localStorage.setItem('pending_bundle_funding', JSON.stringify({
          bundleId: bundle.id,
          amountPerWallet,
          walletCount: Math.min(bundle.wallets.length, 20),
          timestamp: Date.now()
        }));
        
        // Call the single transaction method
        const result = await bundlerService.fundWalletsSingleTransaction(
          walletPubKey,
          amountPerWallet,
          async (tx: Transaction) => {
            console.log('Mobile: Preparing to sign single transaction...');
            
            // Store the transaction for processing after redirect
            localStorage.setItem('pending_funding_tx', JSON.stringify({
              bundleId: bundle.id,
              amountPerWallet,
              walletCount: Math.min(bundle.wallets.length, 20),
              timestamp: Date.now()
            }));
            
            // This will redirect to Phantom
            await solanaService.signTransaction(tx);

            
            // This code won't execute due to redirect
            return tx;
          },
          onProgress
        );
        
        return result;
        
      } catch (error: any) {
        if (error.message?.includes('Redirect')) {
          // This is expected - the transaction will complete after redirect
          console.log('Redirecting to Phantom for signing...');
          return {
            success: false,
            funded: 0,
            failed: 0,
            error: 'Redirecting to Phantom...'
          };
        }
        throw error;
      }
      
    } else {
      // 💻 DESKTOP: Use batch method
      if (!provider?.isPhantom) {
        return { 
          success: false, 
          funded: 0, 
          failed: bundle.wallets.length,
          error: 'Phantom wallet not found. Please ensure Phantom is installed and connected.' 
        };
      }

      console.log('💻 Desktop: Using batch signing...');

      if (provider.signAllTransactions) {
        const result = await bundlerService.fundWalletsBatched(
          walletPubKey,
          amountPerWallet,
          async (txs: Transaction[]) => {
            console.log(`Desktop: Signing ${txs.length} transactions...`);
            return await provider.signAllTransactions(txs);
          },
          2000,
          onProgress
        );
        return result;
      } else {
        // Fallback to sequential
        const result = await bundlerService.fundWalletsSequential(
          walletPubKey,
          amountPerWallet,
          async (tx: Transaction) => {
            return await provider.signTransaction(tx);
          },
          2000,
          onProgress
        );
        return result;
      }
    }

  } catch (error: any) {
    console.error('Funding error details:', error);
    
    // Handle redirect error gracefully
    if (error.message?.includes('Redirect') || error.message === 'Redirected') {
      return {
        success: false,
        funded: 0,
        failed: 0,
        error: 'Redirecting to Phantom for signing...'
      };
    }
    
    return { 
      success: false, 
      funded: 0, 
      failed: bundle.wallets.length,
      error: error.message || 'Unknown error occurred' 
    };
  }
};

  const unlockBundle = async (bundleId: string) => {
    setPasswordAction('unlock');
    setCurrentBundle({ id: bundleId });
    setShowPasswordModal(true);
  };

  const handleUnlock = async () => {
  if (!currentBundle || !password) return;

  if (password.length < MIN_PASSWORD_LENGTH) {
    alert(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
    return;
  }

  try {
    const bundle = await bundlerService.unlockBundle(currentBundle.id, password);
    setCurrentBundle(bundle);
    setIsUnlocked(true);
    setBundlerStatus('ready');
    setShowPasswordModal(false);
    resetPasswordFields();
    setActiveTab('trade');
  } catch (error: any) {
    alert(`Failed to unlock: ${error.message}`);
  }
};

  const lockBundle = () => {
    bundlerService.lockBundle();
    setIsUnlocked(false);
    setCurrentBundle(null);
    setBundlerStatus('idle');
  };

  const deleteBundle = async (bundleId: string, bundleName: string) => {
  // Only allow deletion if bundle is unlocked
  if (currentBundle?.id === bundleId && !isUnlocked) {
    alert('Please unlock the bundle before deleting');
    return;
  }

  // Safety confirmation with bundle name
  const confirmText = window.prompt(
    `⚠️ This will permanently delete the bundle "${bundleName}" and all its wallets.\n\n` +
    `Type the bundle name to confirm deletion:`,
    ''
  );

  if (confirmText !== bundleName) {
    if (confirmText !== null) {
      alert('Bundle name does not match. Deletion cancelled.');
    }
    return;
  }

  // Additional safety check for bundles with funds
  const bundleToDelete = bundles.find(b => b.id === bundleId);
  if (bundleToDelete && bundleToDelete.totalBalance > 0.001) {
    const fundConfirm = window.confirm(
      `⚠️ WARNING: This bundle contains ${bundleToDelete.totalBalance.toFixed(4)} SOL!\n\n` +
      `Make sure you have withdrawn all funds before deleting.\n\n` +
      `Continue with deletion?`
    );
    
    if (!fundConfirm) return;
  }

  try {
    // Update UI state immediately (optimistic update)
    setBundles(prevBundles => prevBundles.filter(b => b.id !== bundleId));
    
    // Update stats immediately
    const filteredBundles = bundles.filter(b => b.id !== bundleId);
    const stats = {
      totalBundles: filteredBundles.length,
      totalWallets: filteredBundles.reduce((sum, b) => sum + b.wallets.length, 0),
      totalBalance: filteredBundles.reduce((sum, b) => sum + b.totalBalance, 0),
      activeWallets: filteredBundles.reduce((sum, b) =>
        sum + b.wallets.filter((w: any) => w.funded).length, 0
      )
    };
    setBundlerStats(stats);
    
    // Clear the current bundle since we're deleting it
    setCurrentBundle(null);
    setIsUnlocked(false);
    setBundlerStatus('idle');
    
    // Now delete from storage
    await bundlerService.deleteBundle(bundleId);
    
    alert(`Bundle "${bundleName}" has been deleted successfully.`);
    
  } catch (error: any) {
    console.error('Failed to delete bundle:', error);
    // Revert the optimistic update on error
    await loadUserBundles();
    alert(`Failed to delete bundle: ${error.message}`);
  }
};

  const executePurchase = async () => {
    if (!currentBundle || !isUnlocked) {
      alert('Please unlock a bundle first');
      return;
    }

    if (!tokenAddress || !tradeAmount) {
      alert('Please enter token address and amount');
      return;
    }

    setBundlerStatus('purchasing');
    setProgress(0);

    try {
      const result = await bundlerService.executeBundlePurchase(
        tokenAddress,
        parseFloat(tradeAmount),
        parseFloat(slippage),
        (prog, status) => {
          setProgress(prog);
          console.log(status);
        }
      );

      if (result.success) {
        alert(`✅ Purchase complete!\nSuccessful: ${result.successful}\nFailed: ${result.failed}`);
      } else {
        alert(`⚠️ Purchase failed: ${result.error}`);
      }

      setBundlerStatus('ready');
      await bundlerService.refreshBalances();

    } catch (error: any) {
      console.error('Purchase error:', error);
      alert(`Purchase failed: ${error.message}`);
      setBundlerStatus('ready');
    }
  };

  const withdrawFunds = async () => {
    if (!currentBundle || !isUnlocked) {
      alert('Please unlock a bundle first');
      return;
    }

    const pubKey = getPublicKeyObj(publicKey);
    if (!pubKey) {
      alert('Wallet not connected properly');
      return;
    }

    setBundlerStatus('withdrawing');
    setProgress(0);

    try {
      const result = await bundlerService.withdrawAllFunds(
        pubKey,
        (prog: number) => setProgress(prog)
      );

      if (result.success) {
        alert(`✅ Withdrawal complete!\nTotal withdrawn: ${result.totalWithdrawn.toFixed(4)} SOL`);
        await loadUserBundles();
      } else {
        alert(`⚠️ Withdrawal failed: ${result.error}`);
      }

      setBundlerStatus('ready');

    } catch (error: any) {
      console.error('Withdrawal error:', error);
      alert(`Withdrawal failed: ${error.message}`);
      setBundlerStatus('ready');
    }
  };

  

 const topUpWallets = async () => {
  if (!currentBundle || !isUnlocked) {
    alert('Please unlock a bundle first');
    return;
  }

  const amountStr = window.prompt(
    'Enter amount of SOL to add to each wallet:\n' +
    '(Recommended: 0.05-0.1 SOL for trading + fees)',
    '0.05'
  );
  
  if (!amountStr) return;
  
  const amount = parseFloat(amountStr);
  if (isNaN(amount) || amount <= 0) {
    alert('Invalid amount');
    return;
  }

  // Limit check for mobile
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const walletsToFund = isMobile ? Math.min(currentBundle.wallets.length, 5) : currentBundle.wallets.length;
  const totalNeeded = amount * walletsToFund;
  
  const confirm = window.confirm(
    `This will add ${amount} SOL to each of the first ${walletsToFund} wallets.\n` +
    `Total needed: ${totalNeeded.toFixed(4)} SOL (+ network fees ~${(0.0001 * walletsToFund).toFixed(4)} SOL)\n\n` +
    `Continue?`
  );

  if (!confirm) return;

  setBundlerStatus('funding');
  setProgress(0);

  try {
    const result = await fundBundleWallets(
      amount,
      (prog: number) => setProgress(prog)
    );

    if (result?.success) {
      alert(`✅ Top-up complete!\n${result.funded} wallets funded with ${amount} SOL each.`);
      setBundlerStatus('ready');
      await bundlerService.refreshBalances();
      await loadUserBundles();
    } else if (result?.error?.includes('Redirect')) {
      // Don't show error for redirects on mobile
      console.log('Redirecting to Phantom...');
    } else {
      alert(`⚠️ Top-up failed: ${result?.error ?? 'Unknown error'}`);
      setBundlerStatus('idle');
    }

  } catch (error: any) {
    console.error('Top-up error:', error);
    if (!error.message?.includes('Redirect')) {
      alert(`Failed to top up wallets: ${error.message}`);
    }
    setBundlerStatus('idle');
  }
};

 // Replace your fundBundleWallets function with this TypeScript-safe version:

const fundBundleWallets = async (
  amountPerWallet: number,
  onProgress?: (progress: number) => void
): Promise<{ success: boolean; funded: number; failed: number; error?: string }> => {
  console.log('Starting fundBundleWallets...'); 
  
  if (!currentBundle) {
    return { success: false, funded: 0, failed: 0, error: 'No bundle selected' };
  }
  
  if (!publicKey) {
    return { success: false, funded: 0, failed: 0, error: 'No wallet connected' };
  }

  try {
    const walletAddress = getAddressString(publicKey);
    const walletPubKey = new PublicKey(walletAddress);
    
    console.log('Wallet public key:', walletPubKey.toBase58());
    console.log('Number of wallets to fund:', currentBundle.wallets.length);

    // Check if we're on mobile
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const provider = (window as any).solana;
    const isInPhantomBrowser = !!(provider?.isPhantom && isMobile);

    if (isMobile && !isInPhantomBrowser) {
      // Mobile: Use single transaction method
      console.log('📱 Mobile detected: Using single transaction for all wallets');
      
      // Check wallet count limit for mobile
      if (currentBundle.wallets.length > 5) {
        const proceed = window.confirm(
          `Mobile can only fund up to 5 wallets at once.\n` +
          `You have ${currentBundle.wallets.length} wallets.\n\n` +
          `Would you like to fund the first 5 wallets?`
        );
        
        if (!proceed) {
          return {
            success: false,
            funded: 0,
            failed: currentBundle.wallets.length,
            error: 'Cancelled by user'
          };
        }
      }
      
      // FIX: Properly sign the transaction using solanaService
      const result = await bundlerService.fundWalletsSingleTransaction(
        walletPubKey,
        amountPerWallet,
        async (tx: Transaction) => {
          console.log('Mobile: Signing transaction via solanaService...');
          
          // This will handle the deeplink signing properly
          try {
            await solanaService.signTransaction(tx);
            // On mobile, this will redirect and throw
            return tx;
          } catch (error: any) {
            if (error.message?.includes('Redirect')) {
              // Expected for mobile
              throw error;
            }
            throw new Error(`Failed to sign transaction: ${error.message}`);
          }
        },
        onProgress
      );
      
      return result;
      
    } else {
      // Desktop: Use existing batch method
      if (!provider?.isPhantom) {
        return { 
          success: false, 
          funded: 0, 
          failed: currentBundle.wallets.length,
          error: 'Phantom wallet not found. Please ensure Phantom is installed and connected.' 
        };
      }

      console.log('💻 Desktop: Using batch signing...');

      if (provider.signAllTransactions) {
        const result = await bundlerService.fundWalletsBatched(
          walletPubKey,
          amountPerWallet,
          async (txs: Transaction[]) => {
            console.log(`Desktop: Signing ${txs.length} transactions...`);
            return await provider.signAllTransactions(txs);
          },
          2000,
          onProgress
        );
        return result;
      } else {
        const result = await bundlerService.fundWalletsSequential(
          walletPubKey,
          amountPerWallet,
          async (tx: Transaction) => {
            console.log('Desktop: Signing single transaction...');
            return await provider.signTransaction(tx);
          },
          2000,
          onProgress
        );
        return result;
      }
    }

  } catch (error: any) {
    console.error('Funding error details:', error);
    
    // Handle redirect gracefully for mobile
    if (error.message?.includes('Redirect')) {
      console.log('Redirecting to Phantom for signing...');
      return {
        success: false,
        funded: 0,
        failed: 0,
        error: 'Redirecting to Phantom...'
      };
    }
    
    return { 
      success: false, 
      funded: 0, 
      failed: currentBundle.wallets.length,
      error: error.message || 'Unknown error occurred' 
    };
  }
};

// Also update the checkWalletConnection function to avoid TypeScript errors:
const checkWalletConnection = () => {
  console.log('Checking wallet connection...');
  console.log('isConnected:', isConnected);
  console.log('publicKey:', publicKey);
  console.log('publicKey type:', typeof publicKey);
  
  if (publicKey) {
    // Since publicKey is likely always a string from your wallet context
    const address = getAddressString(publicKey);
    console.log('Wallet address:', address);
    
    try {
      const pubKeyObj = new PublicKey(address);
      console.log('Valid PublicKey:', pubKeyObj.toBase58());
    } catch (e) {
      console.error('Invalid public key:', e);
    }
  }
  
  const provider = (window as any).solana;
  console.log('Phantom provider exists:', !!provider);
  console.log('Is Phantom:', provider?.isPhantom);
  console.log('Provider connected:', provider?.isConnected);
  console.log('Provider publicKey:', provider?.publicKey?.toBase58());
};


  const resetPasswordFields = () => {
    setPassword('');
    setConfirmPassword('');
    setBundleName('');
    setShowPassword(false);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
  };

  if (!isConnected) {
    return (
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-8 border border-gray-700 text-center">
        <Wallet className="mx-auto text-yellow-400 mb-4" size={48} />
        <h2 className="text-2xl font-bold mb-2">Connect Your Wallet</h2>
        <p className="text-gray-400">Please connect your wallet to use the bundler tool</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header Section */}
      <div className="text-center">
        <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
          Advanced Bundler
        </h1>
        <p className="text-xl text-gray-400 max-w-3xl mx-auto">
          Multi-wallet trading system with Jupiter integration
        </p>
      </div>

      {/* Stats Cards */}
<div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-8">
  <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-6 border border-gray-700 hover:border-purple-500 transition-all">
    <PieChart className="w-5 sm:w-8 h-5 sm:h-8 text-blue-400 mb-2 sm:mb-3" />
    <div className="text-lg sm:text-2xl font-bold mb-0.5 sm:mb-1">{bundlerStats.totalBundles}</div>
    <div className="text-xs sm:text-sm text-gray-400">Total Bundles</div>
  </div>
  
  <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-6 border border-gray-700 hover:border-purple-500 transition-all">
    <Wallet className="w-5 sm:w-8 h-5 sm:h-8 text-purple-400 mb-2 sm:mb-3" />
    <div className="text-lg sm:text-2xl font-bold mb-0.5 sm:mb-1">{bundlerStats.totalWallets}</div>
    <div className="text-xs sm:text-sm text-gray-400">Total Wallets</div>
  </div>
  
  <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-6 border border-gray-700 hover:border-purple-500 transition-all">
    <Activity className="w-5 sm:w-8 h-5 sm:h-8 text-green-400 mb-2 sm:mb-3" />
    <div className="text-lg sm:text-2xl font-bold mb-0.5 sm:mb-1">{bundlerStats.activeWallets}</div>
    <div className="text-xs sm:text-sm text-gray-400">Active Wallets</div>
  </div>
  
  <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-6 border border-gray-700 hover:border-purple-500 transition-all">
    <DollarSign className="w-5 sm:w-8 h-5 sm:h-8 text-yellow-400 mb-2 sm:mb-3" />
    <div className="text-lg sm:text-2xl font-bold mb-0.5 sm:mb-1">{bundlerStats.totalBalance.toFixed(2)}</div>
    <div className="text-xs sm:text-sm text-gray-400">Total SOL</div>
  </div>
</div>

      {/* Current Bundle Status */}
      {currentBundle && (
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h3 className="text-xl font-bold flex items-center gap-2">
                {currentBundle.name}
                <span className={`px-2 py-1 text-xs rounded-full ${
                  isUnlocked 
                    ? 'bg-green-500/20 text-green-400' 
                    : 'bg-red-500/20 text-red-400'
                }`}>
                  {isUnlocked ? '🔓 Unlocked' : '🔒 Locked'}
                </span>
              </h3>
              <p className="text-sm text-gray-400 mt-1">
                {currentBundle.wallets?.length || 0} wallets • 
                {currentBundle.totalBalance?.toFixed(4) || 0} SOL • 
                {currentBundle.wallets?.filter((w: any) => w.funded).length || 0} active
              </p>
            </div>
            <div className="flex gap-2">
              {isUnlocked ? (
                <>
                  <button
                    onClick={lockBundle}
                    className="px-4 py-2 bg-red-600/20 text-red-400 rounded-lg hover:bg-red-600/30 transition-all"
                  >
                    <Lock className="w-4 h-4" />
                  </button>
                  <button
                    onClick={withdrawFunds}
                    className="px-4 py-2 bg-yellow-600/20 text-yellow-400 rounded-lg hover:bg-yellow-600/30 transition-all"
                  >
                    Withdraw All
                  </button>
                  <button
                    onClick={() => bundlerService.refreshBalances()}
                    className="px-4 py-2 bg-gray-700/50 text-gray-300 rounded-lg hover:bg-gray-700 transition-all"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </>
              ) : (
                <button
                  onClick={() => unlockBundle(currentBundle.id)}
                  className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg font-medium hover:opacity-90 transition-opacity"
                >
                  Unlock Bundle
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex gap-1 sm:gap-2 overflow-x-auto pb-2">
        {['overview', 'create', 'manage', 'trade'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab as any)}
            className={`px-3 sm:px-6 py-2 sm:py-3 rounded-md sm:rounded-lg font-medium text-xs sm:text-base capitalize transition-all whitespace-nowrap ${
              activeTab === tab
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                : 'bg-gray-800/50 text-gray-400 hover:bg-gray-700/50 hover:text-white border border-gray-700'
            }`}
          >
            {tab === 'trade' && currentBundle && isUnlocked && (
              <span className="inline-block w-1.5 sm:w-2 h-1.5 sm:h-2 bg-green-400 rounded-full mr-1 sm:mr-2 animate-pulse"></span>
            )}
            {tab}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {!currentBundle ? (
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-12 border border-gray-700 text-center">
                <Shield className="mx-auto text-gray-600 mb-4" size={64} />
                <h3 className="text-2xl font-bold mb-2">Get Started with Bundler</h3>
                <p className="text-gray-400 mb-6 max-w-md mx-auto">
                  Create a bundle of wallets for advanced trading strategies and multi-wallet operations
                </p>
                <button
                  onClick={() => setActiveTab('create')}
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg font-medium hover:opacity-90 transition-opacity"
                >
                  Create Your First Bundle
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <button
                  onClick={() => setActiveTab('manage')}
                  className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700 hover:border-purple-500 transition-all group text-left"
                >
                  <Package className="w-10 h-10 text-purple-400 mb-4 group-hover:scale-110 transition-transform" />
                  <h4 className="text-lg font-semibold mb-2">Manage Bundles</h4>
                  <p className="text-gray-400 text-sm">View and manage all your wallet bundles</p>
                </button>
                
                <button
                  onClick={() => setActiveTab('trade')}
                  disabled={!isUnlocked}
                  className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700 hover:border-purple-500 transition-all group text-left disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <TrendingUp className="w-10 h-10 text-green-400 mb-4 group-hover:scale-110 transition-transform" />
                  <h4 className="text-lg font-semibold mb-2">Trade with Jupiter</h4>
                  <p className="text-gray-400 text-sm">Execute trades across all wallets</p>
                </button>
              </div>
            )}

            {/* Quick Stats */}
            {bundles.length > 0 && (
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
                <h3 className="text-lg font-semibold mb-4">Bundle Overview</h3>
                <div className="space-y-3">
                  {bundles.slice(0, 3).map((bundle) => (
                    <div key={bundle.id} className="flex justify-between items-center p-3 bg-gray-900/30 rounded-lg">
                      <div>
                        <p className="font-medium">{bundle.name}</p>
                        <p className="text-sm text-gray-400">
                          {bundle.wallets.length} wallets • {bundle.totalBalance.toFixed(4)} SOL
                        </p>
                      </div>
                      <button
                        onClick={() => unlockBundle(bundle.id)}
                        className="px-3 py-1 bg-purple-600/20 text-purple-400 rounded-lg hover:bg-purple-600/30 transition-all text-sm"
                      >
                        Use
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Create Tab */}
        {activeTab === 'create' && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
              <h3 className="text-xl font-bold mb-6">Create New Bundle</h3>
              
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Number of Wallets
                    </label>
                    <input
                      type="number"
                      value={bundlerConfig.walletCount}
                     onChange={(e) => {
  const value = e.target.value;
  setBundlerConfig({
    ...bundlerConfig,
    walletCount: value === '' ? '' : (parseInt(value) || 1)
  });
}}
                      className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-4 py-3 focus:outline-none focus:border-purple-500 transition-colors"
                      min="1"
                      max="50"
                    />
                    <p className="text-xs text-gray-500 mt-1">Between 1-50 wallets</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Initial Funding (SOL per wallet)
                    </label>
                    <input
                      type="number"
                      value={bundlerConfig.amount}
                      onChange={(e) => {
  const value = e.target.value;
  setBundlerConfig({
    ...bundlerConfig,
    amount: value === '' ? '' : (parseFloat(value) || 0)
  });
}}
                      className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-4 py-3 focus:outline-none focus:border-purple-500 transition-colors"
                      min="0"
                      step="0.01"
                    />
                    <p className="text-xs text-gray-500 mt-1">Leave at 0 to fund later</p>
                  </div>
                </div>

                <div className="p-4 bg-gray-900/30 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="font-medium">Stealth Mode</label>
                      <p className="text-xs text-gray-500">Add random delays between transactions</p>
                    </div>
                    <button
                      onClick={() => setBundlerConfig({
                        ...bundlerConfig,
                        stealthMode: !bundlerConfig.stealthMode
                      })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        bundlerConfig.stealthMode ? 'bg-purple-600' : 'bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          bundlerConfig.stealthMode ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {bundlerConfig.amount > 0 && (
                  <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <p className="text-sm text-yellow-400 flex items-start gap-2">
                      <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>
                       Total funding needed: {((Number(bundlerConfig.walletCount) || 0) * (Number(bundlerConfig.amount) || 0)).toFixed(2)} SOL
                        (+ network fees)
                      </span>
                    </p>
                  </div>
                )}

                <button
                  onClick={handleCreateBundle}
                  disabled={bundlerStatus !== 'idle'}
                  className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {bundlerStatus === 'idle' ? (
                    <>
                      <Plus className="w-5 h-5" />
                      Create Bundle
                    </>
                  ) : (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Processing...
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}


{activeTab === 'manage' && (
  <div className="space-y-4">
    {bundles.length > 0 ? (
      bundles.map((bundle) => (
        <div
          key={bundle.id}
          className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700 hover:border-purple-500 transition-all"
        >
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-lg font-semibold">{bundle.name}</h3>
                {currentBundle?.id === bundle.id && (
                  <span className="px-2 py-1 bg-purple-500/20 text-purple-400 text-xs rounded-full">
                    Active
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                <span>{bundle.wallets.length} wallets</span>
                <span>{bundle.totalBalance.toFixed(4)} SOL</span>
                <span>{bundle.wallets.filter((w: any) => w.funded).length} active</span>
                <span>{new Date(bundle.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
            
            <div className="flex gap-2">
              {currentBundle?.id !== bundle.id ? (
                <>
                  <button
                    onClick={() => unlockBundle(bundle.id)}
                    className="px-4 py-2 bg-purple-600/20 text-purple-400 rounded-lg hover:bg-purple-600/30 transition-all"
                  >
                    Use
                  </button>
                  {/* No delete button for non-active bundles */}
                </>
              ) : (
                <>
                  {/* Delete button only shows when bundle is active AND unlocked */}
                  {isUnlocked ? (
                    <button
                      onClick={() => deleteBundle(bundle.id, bundle.name)}
                      className="px-4 py-2 bg-red-600/20 text-red-400 rounded-lg hover:bg-red-600/30 transition-all flex items-center gap-1"
                      title="Delete Bundle"
                    >
                      <Trash2 size={16} />
                      <span className="hidden sm:inline">Delete</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => alert('Please unlock the bundle first to delete it')}
                      className="px-4 py-2 bg-gray-600/20 text-gray-400 rounded-lg cursor-not-allowed flex items-center gap-1"
                      title="Unlock to delete"
                    >
                      <Trash2 size={16} />
                      <span className="hidden sm:inline">Unlock to Delete</span>
                    </button>
                  )}
                </>
              )}
              <button
                onClick={() => setShowDetails(showDetails === bundle.id ? null : bundle.id)}
                className="px-4 py-2 bg-gray-700/50 text-gray-300 rounded-lg hover:bg-gray-700 transition-all"
              >
                {showDetails === bundle.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
            </div>
          </div>

                  {/* Expandable Details */}
                  {showDetails === bundle.id && (
                    <div className="mt-4 pt-4 border-t border-gray-700">
                      <div className="mb-3 flex justify-between items-center">
                        <span className="text-sm text-gray-400">Wallet Details</span>
                        <div className="flex gap-2">
                          <span className="text-xs px-2 py-1 bg-green-500/20 text-green-400 rounded">
                            Funded: {bundle.wallets.filter((w: any) => w.balance >= 0.003).length}
                          </span>
                          <span className="text-xs px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded">
                            Low: {bundle.wallets.filter((w: any) => w.balance > 0 && w.balance < 0.003).length}
                          </span>
                          <span className="text-xs px-2 py-1 bg-red-500/20 text-red-400 rounded">
                            Empty: {bundle.wallets.filter((w: any) => w.balance === 0).length}
                          </span>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                        {bundle.wallets.map((wallet: any, idx: number) => (
                          <div
                            key={wallet.id}
                            className={`flex items-center justify-between p-2 rounded-lg text-sm ${
                              wallet.balance >= 0.1 
                                ? 'bg-green-900/20 border border-green-800/30'
                                : wallet.balance >= 0.003
                                ? 'bg-gray-900/30 border border-gray-800/30'
                                : wallet.balance > 0
                                ? 'bg-yellow-900/20 border border-yellow-800/30'
                                : 'bg-red-900/20 border border-red-800/30'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-gray-500">#{idx + 1}</span>
                              <span className="font-mono text-xs">{formatAddress(wallet.address)}</span>
                              <span className={`w-2 h-2 rounded-full ${
                                wallet.balance >= 0.1 
                                  ? 'bg-green-400'
                                  : wallet.balance >= 0.003
                                  ? 'bg-blue-400'
                                  : wallet.balance > 0
                                  ? 'bg-yellow-400'
                                  : 'bg-red-400'
                              }`}></span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`font-medium ${
                                wallet.balance >= 0.003 ? 'text-green-400' : 'text-yellow-400'
                              }`}>
                                {wallet.balance.toFixed(4)} SOL
                              </span>
                              <button
                                onClick={() => copyToClipboard(wallet.address)}
                                className="text-gray-400 hover:text-white"
                              >
                                <Copy size={14} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                      {currentBundle?.id === bundle.id && isUnlocked && (
                        <div className="mt-4 p-3 bg-gray-900/30 rounded-lg">
                          <div className="flex justify-between items-center">
                            <div className="text-sm">
                              <p className="text-gray-400">Quick Actions</p>
                              <p className="text-xs text-gray-500 mt-1">
                                Min balance for trading: 0.003 SOL per wallet
                              </p>
                            </div>
                            <button
                              onClick={topUpWallets}
                              className="px-3 py-1 bg-green-600/20 text-green-400 rounded hover:bg-green-600/30 transition-all text-sm"
                            >
                              Top Up All
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-12 border border-gray-700 text-center">
                <Package className="mx-auto text-gray-600 mb-4" size={64} />
                <h3 className="text-2xl font-bold mb-2">No Bundles Yet</h3>
                <p className="text-gray-400 mb-6">Create your first bundle to start trading</p>
                <button
                  onClick={() => setActiveTab('create')}
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg font-medium hover:opacity-90 transition-opacity"
                >
                  Create Bundle
                </button>
              </div>
            )}
          </div>
        )}

        {/* Trade Tab */}
        {activeTab === 'trade' && (
          <div className="max-w-4xl mx-auto">
            <div className="space-y-6">
              {/* Bundle Selection */}
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
                <h3 className="text-lg font-semibold mb-4">Select Active Bundle</h3>
                
                {bundles.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {bundles.map((bundle) => {
                      const walletCount = bundle.wallets.length;
                      const fundedWallets = bundle.wallets.filter((w: any) => w.balance >= 0.003);
                      const sufficientForFees = bundle.wallets.filter((w: any) => w.balance >= (parseFloat(tradeAmount) + 0.0008));
                      
                      return (
                        <button
                          key={bundle.id}
                          onClick={() => {
                            if (currentBundle?.id === bundle.id && isUnlocked) {
                              // Already selected and unlocked
                              return;
                            }
                            setCurrentBundle(bundle);
                            setIsUnlocked(false);
                            setBundlerStatus('idle');
                          }}
                          className={`p-4 rounded-lg border transition-all text-left ${
                            currentBundle?.id === bundle.id
                              ? 'border-purple-500 bg-purple-500/10'
                              : 'border-gray-700 bg-gray-900/30 hover:border-gray-600'
                          }`}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h4 className="font-semibold">{bundle.name}</h4>
                              <p className="text-xs text-gray-400 mt-1">
                                {walletCount} wallets • {bundle.totalBalance.toFixed(4)} SOL
                              </p>
                            </div>
                            {currentBundle?.id === bundle.id && (
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                isUnlocked 
                                  ? 'bg-green-500/20 text-green-400' 
                                  : 'bg-yellow-500/20 text-yellow-400'
                              }`}>
                                {isUnlocked ? '🔓 Unlocked' : '🔒 Locked'}
                              </span>
                            )}
                          </div>
                          
                          <div className="space-y-1">
                            <div className="flex justify-between text-xs">
                              <span className="text-gray-500">Funded wallets:</span>
                              <span className={fundedWallets.length > 0 ? 'text-green-400' : 'text-red-400'}>
                                {fundedWallets.length}/{walletCount}
                              </span>
                            </div>
                            {parseFloat(tradeAmount) > 0 && (
                              <div className="flex justify-between text-xs">
                                <span className="text-gray-500">Can afford trade + fees:</span>
                                <span className={sufficientForFees.length > 0 ? 'text-green-400' : 'text-red-400'}>
                                  {sufficientForFees.length}/{walletCount}
                                </span>
                              </div>
                            )}
                          </div>
                          
                          {currentBundle?.id === bundle.id && !isUnlocked && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                unlockBundle(bundle.id);
                              }}
                              className="mt-3 w-full px-3 py-1.5 bg-gradient-to-r from-purple-600 to-pink-600 rounded text-sm font-medium hover:opacity-90"
                            >
                              Unlock to Trade
                            </button>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-400 mb-4">No bundles available</p>
                    <button
                      onClick={() => setActiveTab('create')}
                      className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg font-medium hover:opacity-90"
                    >
                      Create Bundle
                    </button>
                  </div>
                )}
              </div>

              {currentBundle && isUnlocked && (
                <>
                  {/* Fee Check Warning */}
                  {(() => {
                    const MIN_BALANCE_WITH_FEES = parseFloat(tradeAmount) + 0.0008; 
                    const walletsWithSufficientBalance = currentBundle.wallets.filter(
                      (w: any) => w.balance >= MIN_BALANCE_WITH_FEES
                    );
                    const walletsWithInsufficientBalance = currentBundle.wallets.filter(
                      (w: any) => w.balance > 0 && w.balance < MIN_BALANCE_WITH_FEES
                    );
                    
                    if (walletsWithInsufficientBalance.length > 0) {
                      return (
                        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-sm text-yellow-400 font-medium">Insufficient Balance Warning</p>
                              <p className="text-xs text-yellow-400/80 mt-1">
                                {walletsWithInsufficientBalance.length} wallet(s) have balance but not enough for trade + fees.
                                Each wallet needs at least {MIN_BALANCE_WITH_FEES.toFixed(4)} SOL 
                                ({tradeAmount} SOL trade + ~0.0008 SOL fees).
                              </p>
                              <p className="text-xs text-yellow-400/80 mt-1">
                                Only {walletsWithSufficientBalance.length} wallet(s) can execute this trade.
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {/* Token Selection */}
                  <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
                    <h3 className="text-lg font-semibold mb-4">Select Token</h3>
                    
                    

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Token Address
                      </label>
                      <input
                        type="text"
                        value={tokenAddress}
                        onChange={(e) => setTokenAddress(e.target.value)}
                        className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-4 py-3 focus:outline-none focus:border-purple-500 transition-colors font-mono text-sm"
                        placeholder="Enter token mint address..."
                      />
                    </div>

                    {selectedToken && (
                      <div className="mt-3 p-3 bg-gray-900/30 rounded-lg">
                        <p className="text-sm">
                          <span className="text-gray-500">Selected:</span>
                          <span className="ml-2 font-medium">{selectedToken.name} ({selectedToken.symbol})</span>
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Trade Configuration */}
                  <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
                    <h3 className="text-lg font-semibold mb-4">Trade Configuration</h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Amount per Wallet (SOL)
                        </label>
                        <input
                          type="number"
                          value={tradeAmount}
                          onChange={(e) => setTradeAmount(e.target.value)}
                          className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-4 py-3 focus:outline-none focus:border-purple-500 transition-colors"
                          min="0.01"
                          step="0.01"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Recommended: Keep 0.0008 SOL per wallet for transaction fees
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Slippage Tolerance (%)
                        </label>
                        <div className="flex gap-2">
                          {['1', '5', '10', '15'].map((value) => (
                            <button
                              key={value}
                              onClick={() => setSlippage(value)}
                              className={`px-3 py-2 rounded-lg text-sm ${
                                slippage === value
                                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                                  : 'bg-gray-700/50 hover:bg-gray-700 text-gray-300 border border-gray-700'
                              }`}
                            >
                              {value}%
                            </button>
                          ))}
                          <input
                            type="number"
                            value={slippage}
                            onChange={(e) => setSlippage(e.target.value)}
                            className="flex-1 bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500"
                            min="0.1"
                            max="50"
                            step="0.1"
                          />
                        </div>
                      </div>

                      {/* Trade Summary */}
                      <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                        <h4 className="font-medium mb-3">Trade Summary</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-400">Bundle:</span>
                            <span className="font-medium">{currentBundle.name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400">Wallets with sufficient balance:</span>
                            <span className={`font-medium ${
                              currentBundle.wallets.filter((w: any) => w.balance >= (parseFloat(tradeAmount) + 0.0008)).length > 0
                                ? 'text-green-400'
                                : 'text-red-400'
                            }`}>
                              {currentBundle.wallets.filter((w: any) => w.balance >= (parseFloat(tradeAmount) + 0.0008)).length}/{currentBundle.wallets.length}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400">Amount per Wallet:</span>
                            <span className="font-medium">{tradeAmount} SOL</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400">Est. fees per wallet:</span>
                            <span className="font-medium">~0.0008 SOL</span>
                          </div>
                          <div className="flex justify-between text-base font-semibold pt-2 border-t border-purple-500/20">
                            <span>Total Trade Volume:</span>
                            <span className="text-purple-400">
                              {(parseFloat(tradeAmount) * currentBundle.wallets.filter((w: any) => w.balance >= (parseFloat(tradeAmount) + 0.0008)).length).toFixed(2)} SOL
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={executePurchase}
                      disabled={
                        bundlerStatus !== 'ready' || 
                        !tokenAddress || 
                        !tradeAmount ||
                        currentBundle.wallets.filter((w: any) => w.balance >= (parseFloat(tradeAmount) + 0.0008)).length === 0
                      }
                      className="w-full mt-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {bundlerStatus === 'purchasing' ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Executing Trades...
                        </>
                      ) : currentBundle.wallets.filter((w: any) => w.balance >= (parseFloat(tradeAmount) + 0.0008)).length === 0 ? (
                        <>
                          <AlertCircle className="w-5 h-5" />
                          Insufficient Balance
                        </>
                      ) : (
                        <>
                          <Rocket className="w-5 h-5" />
                          Execute Bundle Trade
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}

              {!currentBundle && (
                <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-12 border border-gray-700 text-center">
                  <Package className="mx-auto text-gray-600 mb-4" size={64} />
                  <h3 className="text-2xl font-bold mb-2">No Bundle Selected</h3>
                  <p className="text-gray-400 mb-6">Select a bundle above to start trading</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Progress Bar */}
      {bundlerStatus !== 'idle' && bundlerStatus !== 'ready' && (
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 border border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-400 capitalize">{bundlerStatus}...</span>
            <span className="text-sm text-gray-400">{progress.toFixed(0)}%</span>
          </div>
          <div className="w-full bg-gray-900/50 rounded-full h-2 overflow-hidden">
            <div
              className="bg-gradient-to-r from-purple-600 to-pink-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 p-6 rounded-xl max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">
              {passwordAction === 'create' ? 'Create Bundle' :
                passwordAction === 'unlock' ? 'Unlock Bundle' :
                  'Export Bundle'}
            </h3>

            {passwordAction === 'create' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Bundle Name
                </label>
                <input
                  type="text"
                  value={bundleName}
                  onChange={(e) => setBundleName(e.target.value)}
                  className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-2 focus:outline-none focus:border-purple-500 transition-colors"
                  placeholder="My Trading Bundle"
                />
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Password
              </label>
              <div className="relative">
               <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-2 pr-10 focus:outline-none focus:border-purple-500 transition-colors"
                placeholder="Min 4 characters"  // Changed from "Min 12 characters"
                minLength={4}
              />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {passwordAction === 'create' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Confirm Password
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-2 focus:outline-none focus:border-purple-500 transition-colors"
                />
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  if (passwordAction === 'create') {
                    createBundle();
                  } else if (passwordAction === 'unlock') {
                    handleUnlock();
                  }
                }}
                className="flex-1 py-2 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg font-medium hover:opacity-90 transition-opacity"
              >
                {passwordAction === 'create' ? 'Create' :
                  passwordAction === 'unlock' ? 'Unlock' :
                    'Export'}
              </button>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  resetPasswordFields();
                }}
                className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedTools;